import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import DatePicker from "../../../component-lib/Atoms/DatePicker/DatePicker";
import TooltipForTable from "../../../component-lib/Molecules/tooltip/TooltipForTable";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ReportsService from "../../../services/ReportsService";
import { componentKey } from "../ReportsSlice";
import { getEmsDailyReport } from "../ReportsSaga";
import {
  CSV_HEADERS_EMS_DAILY,
  ROWS_PER_PAGE_OPTIONS,
  formatReportDate,
  formatVisitType,
  toApiStartDate,
  toApiEndDate,
} from "../ReportConstants";
import { destructStatusData } from "../../../utils/statusConstants";
import DownloadCsvButton from "../components/DownloadCsvButton";
import StatusChip from "../components/StatusChip";

type EmsDailyReportTabProps = { facilityId: string };

const EmsDailyReportTab = ({ facilityId }: EmsDailyReportTabProps) => {
  const dispatch = useDispatch();
  const { emsDailyReport } = useSelector(
    (state: Record<string, any>) => state[componentKey] as Record<string, any>
  );

  const [pageNumber, setPageNumber] = useState(1);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);

  const rows: Array<Record<string, any>> = emsDailyReport?.data || [];

  useEffect(() => {
    dispatch(
      getEmsDailyReport({
        pageNumber,
        limit: rowPerPage,
        facilityId: facilityId || undefined,
        startDate: toApiStartDate(startDate),
        endDate: toApiEndDate(endDate),
      })
    );
  }, [facilityId, pageNumber, rowPerPage, startDate, endDate]);

  const isAnyFilterApplied = Boolean(startDate || endDate);

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setPageNumber(1);
  };

  const columns = [
    {
      field: "visitDate",
      label: <div>Visit Date</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {formatReportDate(row?.visitDate)}
        </p>
      ),
    },
    {
      field: "patientName",
      label: <div>Patient Name</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.patientName || "-"}</p>
      ),
    },
    {
      field: "bsaId",
      label: <div>BSA ID</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.bsaId || "-"}</p>
      ),
    },
    {
      field: "visitType",
      label: <div>Visit Type</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {formatVisitType(row?.visitType)}
        </p>
      ),
    },
    {
      field: "incidentNumber",
      label: <div>Incident Number</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {row?.incidentNumber || "-"}
        </p>
      ),
    },
    {
      field: "incidentLocation",
      label: <div>Incident Location</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {row?.incidentLocation || "-"}
        </p>
      ),
    },
    {
      field: "incidentDate",
      label: <div>Incident Date</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">
          {formatReportDate(row?.incidentDate)}
        </p>
      ),
    },
    {
      field: "chiefComplaint",
      label: <div>Chief Complaint</div>,
      renderLogic: (row: Record<string, any>) => (
        <TooltipForTable label={row?.chiefComplaint || "-"} />
      ),
    },
    {
      field: "facilityName",
      label: <div>Facility</div>,
      renderLogic: (row: Record<string, any>) => (
        <p className="text-neutral-90 font-normal">{row?.facilityName || "-"}</p>
      ),
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: Record<string, any>) => (
        <StatusChip status={row?.status} />
      ),
    },
  ];

  const fetchCsvRows = async () => {
    const response = await ReportsService.getEmsDailyReport({
      limit: "All",
      facilityId: facilityId || undefined,
      startDate: toApiStartDate(startDate),
      endDate: toApiEndDate(endDate),
    });
    const data: Array<Record<string, any>> = response?.data?.data || [];
    return data.map((row) => ({
      visitDate: formatReportDate(row?.visitDate),
      patientName: row?.patientName,
      bsaId: row?.bsaId,
      visitType: formatVisitType(row?.visitType),
      incidentNumber: row?.incidentNumber,
      incidentLocation: row?.incidentLocation,
      incidentDate: formatReportDate(row?.incidentDate),
      chiefComplaint: row?.chiefComplaint,
      facilityName: row?.facilityName,
      status: destructStatusData(row?.status),
    }));
  };

  return (
    <div className="mt-5">
      <div className="flex flex-wrap mb-4 gap-3 justify-end items-center">
        <div style={{ minWidth: "180px" }}>
          <DatePicker
            label="From Date"
            value={startDate}
            maxDate={endDate || undefined}
            onChangeCb={(value) => {
              setPageNumber(1);
              setStartDate(value);
            }}
          />
        </div>
        <div style={{ minWidth: "180px" }}>
          <DatePicker
            label="To Date"
            value={endDate}
            minDate={startDate || undefined}
            onChangeCb={(value) => {
              setPageNumber(1);
              setEndDate(value);
            }}
          />
        </div>
        <ClearFiltersButton
          onClear={handleClearFilters}
          disabled={!isAnyFilterApplied}
        />
        <DownloadCsvButton
          headers={CSV_HEADERS_EMS_DAILY}
          filename="ems-daily-report.csv"
          fetchRows={fetchCsvRows}
          disabled={!rows.length}
        />
      </div>

      <Table
        columns={columns}
        rows={rows}
        noDataFoundMessage="No EMS visits found"
        paginationProps={{
          isPagination: true,
          page: pageNumber,
          totalPages: emsDailyReport?.totalPages,
          totalCount: emsDailyReport?.totalRecords,
          limit: rowPerPage,
          rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
          onPageChange: (page: number) => setPageNumber(page),
          handleChangeRowsPerPage: (limitValue: string) => {
            setRowPerPage(limitValue);
            setPageNumber(1);
          },
        }}
      />
    </div>
  );
};

export default EmsDailyReportTab;
